import {
    SkeletonBadge,
    SkeletonButton,
    SkeletonIconTile,
    SkeletonText,
} from '@/components/ui/skeleton';

interface ModuleHeaderSkeletonProps {
    /** Width of the h1 placeholder — roughly the module name's rendered width. */
    readonly titleWidth?: string;
    /** Width of the one-line description under the title. */
    readonly descriptionWidth?: string;
    /** Mirrors the `ResourceUsageChip` the list headers render before the CTA. */
    readonly showUsageChip?: boolean;
    /** Pixel widths of the trailing action buttons, left to right. */
    readonly actionWidths?: readonly number[];
}

/**
 * Loading placeholder for the standard module header: a 56px icon tile, the
 * page title with a caption beneath it, and right-aligned actions.
 *
 * Mirrors the markup of the Sequences / Workflows / Polls list headers exactly
 * — same `size-14 rounded-xl` tile, same `gap-3`, same `mt-0.5` between title
 * and caption — so the header does not move when the real one renders.
 */
export function ModuleHeaderSkeleton({
    titleWidth = '160px',
    descriptionWidth = '420px',
    showUsageChip = false,
    actionWidths = [156],
}: ModuleHeaderSkeletonProps) {
    return (
        <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
                <SkeletonIconTile />
                <div>
                    <SkeletonText size="h1" width={titleWidth} />
                    <SkeletonText size="xs" width={descriptionWidth} className="mt-0.5" />
                </div>
            </div>
            <div className="flex items-center gap-3">
                {showUsageChip && <SkeletonBadge width={150} className="h-6" />}
                {actionWidths.map((width, i) => (
                    <SkeletonButton key={i} width={width} />
                ))}
            </div>
        </div>
    );
}
